// One requestAnimationFrame loop for everything that animates (proposal §6.5): it runs only while
// something is subscribed, and the engine pauses it when the page is hidden. It also keeps the
// recent frame intervals and the time spent in the callbacks, for the frame-time overlay.

export type FrameCallback = (now: number) => void;

export interface FrameStats {
    /** Frames in the measuring window. */
    frames: number;
    framesPerSecond: number;
    /** Intervals between frames, in ms. */
    medianInterval: number;
    p95Interval: number;
    maxInterval: number;
    /** Time spent in the callbacks per frame, in ms. */
    meanWork: number;
    maxWork: number;
}

const WINDOW_MS = 2_000;

const callbacks = new Set<FrameCallback>();
const samples: { time: number; interval: number; work: number }[] = [];
let handle = 0;
let lastFrame: number | undefined;

function frame(now: number) : void {
    handle = 0;
    const start = performance.now();
    for (const callback of [...callbacks]) {
        try {
            callback(now);
        } catch (error) {
            console.error('animation frame callback failed', error);
        }
    }

    const work = performance.now() - start;
    if (lastFrame !== undefined) {
        samples.push({ time: now, interval: now - lastFrame, work });
    }
    lastFrame = now;
    while (samples.length > 0 && samples[0]!.time < now - WINDOW_MS) {
        samples.shift();
    }

    if (callbacks.size > 0) {
        handle = requestAnimationFrame(frame);
    } else {
        lastFrame = undefined;
    }
}

/** Calls `callback` on every frame until the returned function is called. */
export function onAnimationFrame(callback: FrameCallback) : () => void {
    callbacks.add(callback);
    if (handle === 0) {
        handle = requestAnimationFrame(frame);
    }

    return () => {
        callbacks.delete(callback);
    };
}

function percentile(sorted: readonly number[], fraction: number) : number {
    return sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * fraction))] ?? 0;
}

/** The last two seconds of frames. */
export function frameStats() : FrameStats {
    const intervals = samples.map((sample) => sample.interval).sort((a, b) => a - b);
    const works = samples.map((sample) => sample.work);
    const total = samples.reduce((sum, sample) => sum + sample.interval, 0);

    return {
        frames: samples.length,
        framesPerSecond: total > 0 ? (samples.length * 1000) / total : 0,
        medianInterval: percentile(intervals, 0.5),
        p95Interval: percentile(intervals, 0.95),
        maxInterval: intervals.at(-1) ?? 0,
        meanWork: works.length > 0 ? works.reduce((sum, work) => sum + work, 0) / works.length : 0,
        maxWork: works.length > 0 ? Math.max(...works) : 0,
    };
}
